import { useState, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Mail, Phone, Calendar, Award } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';

interface Professional {
  id: string;
  name: string;
  email: string;
  phone: string;
  bio: string;
  photo_url: string;
  credentials: string;
  available_days: string[];
  specialty: {
    name: string;
  };
}

const Team = () => {
  const [professionals, setProfessionals] = useState<Professional[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchProfessionals();
  }, []);

  const fetchProfessionals = async () => {
    try {
      const { data, error } = await supabase
        .from('professionals_2025_11_26_20_41')
        .select(`
          *,
          specialty:specialties_2025_11_26_20_41(name)
        `)
        .eq('is_active', true)
        .order('name');

      if (error) throw error;
      setProfessionals(data || []);
    } catch (error) {
      console.error('Erro ao buscar profissionais:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatAvailableDays = (days: string[]) => {
    const dayNames: { [key: string]: string } = {
      'monday': 'Segunda',
      'tuesday': 'Terça',
      'wednesday': 'Quarta',
      'thursday': 'Quinta',
      'friday': 'Sexta',
      'saturday': 'Sábado',
      'sunday': 'Domingo'
    };

    return days.map(day => dayNames[day] || day).join(', ');
  };

  const scrollToAppointment = () => {
    const element = document.querySelector('#appointment');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  if (loading) {
    return (
      <section id="team" className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center">
            <div className="animate-pulse">
              <div className="h-8 bg-gray-200 rounded w-64 mx-auto mb-4"></div>
              <div className="h-12 bg-gray-200 rounded w-96 mx-auto mb-8"></div>
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section id="team" className="py-20 bg-gray-50">
      <div className="container mx-auto px-4">
        {/* Header da seção */}
        <div className="text-center mb-16">
          <Badge variant="outline" className="mb-4 text-primary border-primary">
            Nossa Equipe
          </Badge>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Profissionais
            <span className="block text-primary">especializados</span>
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Conheça nossa equipe de profissionais qualificados e experientes, 
            dedicados a oferecer o melhor atendimento para você.
          </p>
        </div>

        {/* Grid de profissionais */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {professionals.map((professional) => (
            <Card key={professional.id} className="hover:shadow-medium transition-smooth group">
              <CardContent className="p-6">
                {/* Foto do profissional */}
                <div className="relative mb-6">
                  <div className="w-32 h-32 mx-auto rounded-full overflow-hidden bg-gray-200">
                    {professional.photo_url ? (
                      <img 
                        src={professional.photo_url} 
                        alt={professional.name}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full bg-gradient-primary flex items-center justify-center text-white text-2xl font-bold">
                        {professional.name.split(' ').map(n => n[0]).join('').substring(0, 2)}
                      </div>
                    )}
                  </div>
                  <div className="absolute -bottom-2 left-1/2 transform -translate-x-1/2">
                    <Badge variant="secondary" className="bg-primary text-primary-foreground">
                      {professional.specialty?.name}
                    </Badge>
                  </div>
                </div>

                {/* Informações básicas */}
                <div className="text-center mb-4">
                  <h3 className="text-xl font-semibold text-gray-900 mb-1">
                    {professional.name}
                  </h3>
                  <p className="text-gray-600 text-sm mb-2">
                    {professional.credentials}
                  </p>
                  <p className="text-gray-500 text-sm">
                    Disponível: {formatAvailableDays(professional.available_days)}
                  </p>
                </div>

                {/* Bio resumida */}
                <p className="text-gray-600 text-sm mb-6 line-clamp-3">
                  {professional.bio}
                </p>

                {/* Botões de ação */}
                <div className="flex gap-2">
                  <Dialog>
                    <DialogTrigger asChild>
                      <Button variant="outline" className="flex-1">
                        Ver Perfil
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-2xl">
                      <DialogHeader>
                        <DialogTitle className="text-2xl">
                          {professional.name}
                        </DialogTitle>
                      </DialogHeader>
                      
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        {/* Foto e informações básicas */}
                        <div className="text-center">
                          <div className="w-40 h-40 mx-auto rounded-full overflow-hidden bg-gray-200 mb-4">
                            {professional.photo_url ? (
                              <img 
                                src={professional.photo_url} 
                                alt={professional.name}
                                className="w-full h-full object-cover"
                              />
                            ) : (
                              <div className="w-full h-full bg-gradient-primary flex items-center justify-center text-white text-3xl font-bold">
                                {professional.name.split(' ').map(n => n[0]).join('').substring(0, 2)}
                              </div>
                            )}
                          </div>
                          <Badge className="mb-2">
                            {professional.specialty?.name}
                          </Badge>
                        </div>

                        {/* Detalhes */}
                        <div className="md:col-span-2">
                          <div className="space-y-4">
                            <div>
                              <h4 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
                                <Award className="h-4 w-4" />
                                Credenciais
                              </h4>
                              <p className="text-gray-600">{professional.credentials}</p>
                            </div>

                            <div>
                              <h4 className="font-semibold text-gray-900 mb-2">Biografia</h4>
                              <p className="text-gray-600 leading-relaxed">{professional.bio}</p>
                            </div>

                            <div>
                              <h4 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
                                <Calendar className="h-4 w-4" />
                                Disponibilidade
                              </h4>
                              <p className="text-gray-600">
                                {formatAvailableDays(professional.available_days)}
                              </p>
                            </div>

                            <div className="flex gap-4 pt-4">
                              {professional.email && (
                                <div className="flex items-center gap-2 text-sm text-gray-600">
                                  <Mail className="h-4 w-4" />
                                  <span>{professional.email}</span>
                                </div>
                              )}
                              {professional.phone && (
                                <div className="flex items-center gap-2 text-sm text-gray-600">
                                  <Phone className="h-4 w-4" />
                                  <span>{professional.phone}</span>
                                </div>
                              )}
                            </div>

                            <Button 
                              onClick={scrollToAppointment}
                              className="w-full gradient-primary text-white mt-4"
                            >
                              Agendar com {professional.name.split(' ')[0]}
                            </Button>
                          </div>
                        </div>
                      </div>
                    </DialogContent>
                  </Dialog>

                  <Button 
                    onClick={scrollToAppointment}
                    className="flex-1 gradient-primary text-white"
                  >
                    Agendar
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Seção de destaque da equipe */}
        <div className="mt-16 bg-white rounded-2xl p-8 md:p-12 shadow-soft">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
            <div>
              <h3 className="text-3xl font-bold text-gray-900 mb-6">
                Uma equipe comprometida com sua saúde
              </h3>
              <div className="space-y-4 text-gray-600">
                <p>
                  Nossos profissionais são cuidadosamente selecionados não apenas 
                  por sua competência técnica, mas também por sua capacidade de 
                  criar um ambiente acolhedor e empático.
                </p>
                <p>
                  Cada membro da nossa equipe participa regularmente de programas 
                  de educação continuada, garantindo que estejam sempre atualizados 
                  com as melhores práticas e técnicas mais modernas.
                </p>
              </div>
              
              <div className="grid grid-cols-2 gap-4 mt-8">
                <div className="text-center p-4 bg-primary/5 rounded-lg">
                  <div className="text-2xl font-bold text-primary">15+</div>
                  <div className="text-sm text-gray-600">Profissionais</div>
                </div>
                <div className="text-center p-4 bg-secondary/5 rounded-lg">
                  <div className="text-2xl font-bold text-secondary">50+</div>
                  <div className="text-sm text-gray-600">Anos de Experiência</div>
                </div>
              </div>
            </div>

            <div className="relative">
              <img 
                src="/images/medical_professionals_2.jpeg" 
                alt="Equipe de profissionais"
                className="rounded-lg shadow-medium w-full h-80 object-cover"
              />
              <div className="absolute inset-0 bg-gradient-primary opacity-10 rounded-lg"></div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Team;